import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DashboardsDisplayGridRowDataFragment } from '../../../gen-src/queries/DashboardsDisplayGridRowDataFragment';
import {
  DatabaseDownstreamDashboardsFilteredByTableQuery,
  DatabaseDownstreamDashboardsFilteredByTableQuery_tables_downstreamDashboardsConnection_nodes,
  DatabaseDownstreamDashboardsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamDashboardsFilteredByTableQuery';
import {
  DatabaseDownstreamDashboardsQuery,
  DatabaseDownstreamDashboardsQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamDashboardsQuery';
import { DashboardSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  DatabaseDownstreamDashboardsFilteredByTableQueryGql,
} from '../../queries/DatabaseDownstreamDashboardsFilteredByTableQueryGql';
import { DatabaseDownstreamDashboardsQueryGql } from '../../queries/DatabaseDownstreamDashboardsQueryGql';
import { DashboardsDisplayGrid, DashboardsDisplayGridRowData, getRowDataForDashboard } from '../sharedDisplayGrid/DashboardsDisplayGrid';
import { DashboardsValidSortFields } from '../sorting/sortProviders/DashboardsGridSortProvider';

type QueryVariables = DatabaseDownstreamDashboardsQueryVariables;
type QueryResults = DatabaseDownstreamDashboardsQuery;
type QueryResultListNode = DashboardsDisplayGridRowDataFragment;
type GridRowData = DashboardsDisplayGridRowData;

type QueryVariablesWithFilter = DatabaseDownstreamDashboardsFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamDashboardsFilteredByTableQuery;
type QueryResultListNodeWithFilter = DatabaseDownstreamDashboardsFilteredByTableQuery_tables_downstreamDashboardsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamDashboardsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDashboardsConnection;
}

export type DatabaseDownstreamDashboardsQueryGridProps =
  GridSortProps<DashboardsValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamDashboardsQueryGrid(props: DatabaseDownstreamDashboardsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDashboardsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDashboardsLineageGridMessage();

  return props.filteredTableId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
      queryGql={DatabaseDownstreamDashboardsFilteredByTableQueryGql}
      partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as DashboardSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDashboard}
    >
      {({ rowStore }) => <DashboardsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamDashboardsQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as DashboardSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDashboard}
      >
        {({ rowStore }) => <DashboardsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
